import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_green-tech-hyd/artifacts/aiwksqrp_tech4earth_logo-removebg-preview.png";

const navLinks = [
  { name: "About", href: "#about" },
  { name: "Vision", href: "#vision" },
  { name: "Opportunities", href: "#opportunities" },
  { name: "Initiatives", href: "#initiatives" },
  { name: "Contact", href: "#contact" },
];

const Header = ({ mobileMenuOpen, setMobileMenuOpen }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "glass-header shadow-sm border-b border-stone-200/50"
          : "bg-gradient-to-b from-white via-blue-50/80 to-blue-200/60"
      }`}
      style={!scrolled ? { paddingBottom: '8px' } : {}}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-28">
          {/* Logo */}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center"
            data-testid="header-logo"
          >
            <img
              src={LOGO_URL}
              alt="Tech4Earth Mobility"
              className="h-24 w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className={`text-sm font-medium transition-colors hover:text-emerald-700 ${
                  scrolled ? "text-stone-700" : "text-blue-800"
                }`}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
            <Button
              onClick={(e) => scrollToSection(e, "#contact")}
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-6 btn-shine"
              data-testid="nav-join-button"
            >
              Join Us
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              scrolled ? "text-stone-700 hover:bg-stone-100" : "text-blue-800 hover:bg-blue-50"
            }`}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-stone-200"
            data-testid="mobile-nav"
          >
            <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-2">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-stone-700 hover:text-emerald-700 py-3 px-4 rounded-lg hover:bg-stone-50 transition-colors font-medium"
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </a>
              ))}
              <Button
                onClick={(e) => scrollToSection(e, "#contact")}
                className="mt-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full"
                data-testid="mobile-nav-join-button"
              >
                Join Us
              </Button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
